import { createSelector, OutputSelector, Selector } from '@reduxjs/toolkit';
import { RootState } from 'Data/Redux';

/**
 * Creates a new selector that combines the results of multiple selectors
 *
 * @todo Create a properly memoised selector using re-reselect
 */
export default function combine<A, B, R>(
  selectors: [Selector<RootState, A>, Selector<RootState, B>],
  combiner: (a: A, b: B) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B) => R>;

export default function combine<A, B, C, R>(
  selectors: [Selector<RootState, A>, Selector<RootState, B>, Selector<RootState, C>],
  combiner: (a: A, b: B, c: C) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C) => R>;

export default function combine<A, B, C, D, R>(
  selectors: [Selector<RootState, A>, Selector<RootState, B>, Selector<RootState, C>, Selector<RootState, D>],
  combiner: (a: A, b: B, c: C, d: D) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C, d: D) => R>;

export default function combine<A, B, C, D, E, R>(
  selectors: [
    Selector<RootState, A>,
    Selector<RootState, B>,
    Selector<RootState, C>,
    Selector<RootState, D>,
    Selector<RootState, E>
  ],
  combiner: (a: A, b: B, c: C, d: D, e: E) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C, d: D, e: E) => R>;

export default function combine<A, B, C, D, E, F, R>(
  selectors: [
    Selector<RootState, A>,
    Selector<RootState, B>,
    Selector<RootState, C>,
    Selector<RootState, D>,
    Selector<RootState, E>,
    Selector<RootState, F>
  ],
  combiner: (a: A, b: B, c: C, d: D, e: E, f: F) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C, d: D, e: E, f: F) => R>;

export default function combine<A, B, C, D, E, F, G, R>(
  selectors: [
    Selector<RootState, A>,
    Selector<RootState, B>,
    Selector<RootState, C>,
    Selector<RootState, D>,
    Selector<RootState, E>,
    Selector<RootState, F>,
    Selector<RootState, G>
  ],
  combiner: (a: A, b: B, c: C, d: D, e: E, f: F, g: G) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C, d: D, e: E, f: F, g: G) => R>;

export default function combine<A, B, C, D, E, F, G, H, R>(
  selectors: [
    Selector<RootState, A>,
    Selector<RootState, B>,
    Selector<RootState, C>,
    Selector<RootState, D>,
    Selector<RootState, E>,
    Selector<RootState, F>,
    Selector<RootState, G>,
    Selector<RootState, H>
  ],
  combiner: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H) => R>;

// Extend this pattern of typed overloaded methods as required.

// Arbitrary number of functions (no argument typing)
export default function combine<R>(
  selectors: Selector<RootState, any>[],
  combiner: (...args: any[]) => R
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, (...args: any[]) => R>;

export default function combine(
  selectors: Selector<RootState, any>[],
  combiner: (...args: any[]) => any
): OutputSelector<ReadonlyArray<Selector<RootState>>, any, (...args: any[]) => any> {
  return createSelector(selectors, combiner);
}
